export type PollOutcome = "done" | "retry" | "stop";

type PollWithRetryOptions = {
  maxAttempts: number;
  delayMs: number;
  run: (args: {
    signal: AbortSignal;
    attemptsLeft: number;
  }) => Promise<PollOutcome>;
  onExhausted: () => void;
};

export const pollWithRetry = ({
  maxAttempts,
  delayMs,
  run,
  onExhausted,
}: PollWithRetryOptions) => {
  const controller = new AbortController();
  const isCancelled = () => controller.signal.aborted;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const attempt = async (attemptsLeft: number): Promise<void> => {
    if (isCancelled()) return;

    let outcome: PollOutcome;

    try {
      outcome = await run({ signal: controller.signal, attemptsLeft });
    } catch {
      outcome = "retry";
    }

    if (isCancelled()) return;

    if (outcome !== "retry") return;

    if (attemptsLeft <= 1) {
      onExhausted();

      return;
    }

    timeoutId = setTimeout(() => {
      if (!isCancelled()) {
        void attempt(attemptsLeft - 1);
      }
    }, delayMs);
  };

  void attempt(maxAttempts);

  return () => {
    controller.abort();

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  };
};
